import { ChangeDetectionStrategy, Component, Inject, OnInit, signal } from '@angular/core';
import { PVFloorSpaceDataHelper } from './spaces-details.helper';
import { BehaviorSubject, Observable, combineLatest, distinctUntilKeyChanged, filter, map, tap } from 'rxjs';
import { Floor, Space } from '@miq/domain/pv';
import {
  formatFlatFloorSpaceDeatils,
  getEntity,
  getSpaceId,
  getSpaceUrl,
  hasValidLease,
  isEmptySpace,
} from '../../shared/utils';
import { PVColumn } from '../../shared/column.model';
import { Sort } from '@angular/material/sort';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EntityType } from '@miq/domain';
import { StackPlanViewStateService } from '../../../state/sp-view-state.service';
import moment from 'moment';
import {
  FLOOR_SPACE_DETAILS_STATE_HANDLER_TOKEN,
  FloorSpaceDetailsHandlerSrv,
} from '../floor-space-details-state-handler.service';
import { DASH, isMasked } from '@miq/utils';
import { groupByDisabledUsages } from '../group-usages.const';
import { floorSpaceDetailsTableColumns } from './spaces-details.const';

export interface FloorFlatSpace {
  id: number;
  entity: EntityType | 'NonRentable';
  tenant: string;
  tenantRaw: string[];
  propertyType: string;
  sf: string;
  sfRaw: number;
  leaseType: string;
  endDate: string;
  endDateRaw: string;
  entityUrl: string;
  rowClass: string;
  showFootPrintIcon: boolean;
  disableShowFootPrintIcon?: boolean;
  tenantIdList: number[];
  isSelectedPropertyType: boolean;
}

@UntilDestroy()
@Component({
  selector: 'pv-spaces-details',
  template: `
    <div class="pv-spaces-details-wrapper">
      <ng-container *ngIf="spacesData$ | async as records">
        <ng-container *ngIf="title$ | async as titleComplement">
          <div class="ten-det-title">{{ 'pv.spaceDetails.floor' | xTranslate: { complement: titleComplement } }}</div>
        </ng-container>

        <cbre-table
          tableId="pvFloorSpaceDetails"
          [elements]="records"
          [columns]="columns()"
          [hidePaginator]="true"
          [cellRendererTpl]="cellTpl"
          [cellEventsTpl]="cellEventsTpl"
          [stickyHeader]="true"
          [totalCount]="records.length"
          (sortChange)="onSortChange($event)"
          class="ten-det-table"
        ></cbre-table>
      </ng-container>

      <ng-template #cellTpl let-row="row" let-column="column">{{ row | dataColumnFormatter: column }}</ng-template>
      <ng-template #cellEventsTpl let-row="row">
        <div class="ten-det-table-row-actions">
          <div class="ten-det-row-icon-link-wrapper">
            <a *ngIf="row.isSelectedPropertyType" [href]="row.entityUrl" target="_blank" class="ten-det-row-icon-link">
              <mat-icon
                class="icon ten-det-row-icon ten-det-row-icon--link"
                svgIcon="open_in_new"
                matTooltip="{{ 'commonLabels.openRecordInNewTab' | xTranslate }}"
                matTooltipPosition="left"
              ></mat-icon>
            </a>
          </div>
          <div
            class="ten-det-row-icon-footprint-wrapper"
            matTooltip="{{ 'View Total Footprint' | xTranslate }}"
            matTooltipPosition="left"
          >
            <mat-icon
              *ngIf="row.showFootPrintIcon"
              class="icon ten-det-row-icon ten-det-row-icon--footprint"
              svgIcon="steps"
              (click)="onClickTenant(row)"
              [ngClass]="{ 'ten-det-row-icon--footprint-disabled': row.disableShowFootPrintIcon }"
            ></mat-icon>
          </div>
        </div>
      </ng-template>
    </div>
  `,
  styleUrls: ['./spaces-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PVSpacesDetailsComponent implements OnInit {
  columns = signal<PVColumn[]>([]);
  #title = new BehaviorSubject<string>('');
  title$ = this.#title.asObservable();

  #sortDefaultOptions: Sort = { active: '', direction: '' };
  #sortSelection = new BehaviorSubject<Sort>(this.#sortDefaultOptions);
  sortSelection$ = this.#sortSelection.asObservable();

  dataHelper$: Observable<FloorFlatSpace[]> = null;
  spacesData$: Observable<FloorFlatSpace[]> = null;

  /**
   *
   * @param space
   * @returns true if the row property type is equal to selected property type
   * @returns true if the selected property type is empty and the row is not non-rentable space
   * @returns false if the row property type is not equal to selected property type
   * @returns false if the row is non-rentable space
   */

  #isRowHasSelectedPropertyType = (space: Space): boolean => {
    const currentUsage = this.spViewState.getState().propertyType ?? '';
    if (currentUsage == '' && space.non_rentable_space == null) {
      return true;
    }
    const itemUsage = space.lease?.ref_property_usage_type_desc || space.availability?.ref_property_usage_type_desc;
    if (space.non_rentable_space != null || itemUsage.toLowerCase() != currentUsage.toLowerCase()) {
      return false;
    }
    return true;
  };

  #getSpaceFormattedDate = (space: Space): FloorFlatSpace => {
    if (space == null) {
      return null;
    }
    const sfSize = formatFlatFloorSpaceDeatils.getSF(space);
    const endDate = formatFlatFloorSpaceDeatils.getEndDate(space);
    const tenant = formatFlatFloorSpaceDeatils.getTenant(space);
    const tenantIdList = formatFlatFloorSpaceDeatils.getTenantIds(space);
    const isSelectedPropertyType = this.#isRowHasSelectedPropertyType(space);
    const isNotSelectedPropertyType = !isSelectedPropertyType && space.non_rentable_space == null;
    return {
      id: getSpaceId(space),
      entity: getEntity(space),
      tenant: isNotSelectedPropertyType ? '-' : tenant.formattedValue,
      tenantRaw: isNotSelectedPropertyType ? null : tenant.rawValue,
      propertyType: formatFlatFloorSpaceDeatils.getPropertyType(space),
      sf: sfSize.formattedValue,
      sfRaw: sfSize.rawValue,
      leaseType: isNotSelectedPropertyType ? '-' : formatFlatFloorSpaceDeatils.getLeaseType(space),
      endDate: isNotSelectedPropertyType ? '-' : endDate.formattedValue,
      endDateRaw: isNotSelectedPropertyType ? null : endDate.rawValue,
      entityUrl: getSpaceUrl(space),
      rowClass: isSelectedPropertyType ? '' : 'ten-det-table-row--highlighted',
      showFootPrintIcon: isSelectedPropertyType && hasValidLease(space),
      disableShowFootPrintIcon: tenant.rawValue?.some(t => isMasked(t)),
      tenantIdList,
      isSelectedPropertyType,
    };
  };

  #getFlatSpaces = (floor: Floor): FloorFlatSpace[] => {
    const flatSpaces = ([...(floor?.spaces ?? [])] as Space[])
      .map((space: Space) =>
        isEmptySpace(space)
          ? [null]
          : [
              space.availability?.active_f ? { ...space, lease: null, non_rentable_space: null } : null,
              space.lease?.active_f ? { ...space, availability: null, non_rentable_space: null } : null,
              space.non_rentable_space != null && this.spViewState.getState().showNonRentableSpace !== false
                ? { ...space, availability: null, lease: null }
                : null,
            ]
      )
      .flat()
      .map(this.#getSpaceFormattedDate)
      .filter(Boolean);
    return groupByDisabledUsages(flatSpaces) as FloorFlatSpace[];
  };

  #getFloorLabel = (floor: Floor) => {
    return floor?.floor_number != floor?.floor_desc
      ? [floor?.floor_number, floor?.floor_desc].filter(Boolean).join(' | ')
      : floor?.floor_number;
  };

  private sortFlatSpaces = ([spaces, sort]: [FloorFlatSpace[], Sort]) => {
    return [...spaces].sort((s1, s2) => this.sortFn(s1, s2, sort));
  };
  #sortByDate = (fs1: FloorFlatSpace, fs2: FloorFlatSpace) => {
    const date1 = moment(fs1.endDateRaw).startOf('day');
    const date2 = moment(fs2.endDateRaw).startOf('day');

    if (fs1.endDateRaw == null && fs2.endDateRaw != null) {
      return -1;
    }

    if (fs1.endDateRaw != null && fs2.endDateRaw == null) {
      return 1;
    }

    return date1.isAfter(date2) ? 1 : -1;
  };

  #areDatesEqual = (fs1: FloorFlatSpace, fs2: FloorFlatSpace) => {
    const date1 = moment(fs1.endDateRaw).startOf('day');
    const date2 = moment(fs2.endDateRaw).startOf('day');

    return (fs1.endDateRaw == null && fs2.endDateRaw == null) || date1.isSame(date2);
  };

  /**
   *
   * @param fs1
   * @param fs2
   * @param sort
   * When the dialog opens Then the default table sorting should be by “Property Type” ascending (as described below)
   * When sorted by “Property Type” ascending (either by manual selection, or when the dialog opens),
   *      Then rows will appear in the following order (note: “descending” would follow the same order, in reverse)
   *      rows with the selected Property Type always show first ← note: this is not relevant when “All” is selected in the Property Type toggle
   *      then rows for other Property Types (alphabetical order)
   *      then “Non-Rentable” rows
   * All other column sorting as currently implemented
   * Secondary sort (after sorting by the selected column) should follow the current sorting rule:
   * should by SF, descending, then End Date, then Tenant
   * (MIQ-28824)
   */
  private sortFn = (fs1: FloorFlatSpace, fs2: FloorFlatSpace, sort: Sort) => {
    // primary sorting
    // inactive sort or propertyType with asc sort
    if (!sort.direction || (sort.direction === 'asc' && sort.active === 'propertyType')) {
      if (fs1.isSelectedPropertyType !== fs2.isSelectedPropertyType) {
        // sort by active property type
        if (fs1.isSelectedPropertyType && !fs2.isSelectedPropertyType) {
          return -1;
        }
        if (!fs1.isSelectedPropertyType && fs2.isSelectedPropertyType) {
          return 1;
        }
      }
      // non rentable at the bottom
      if (fs1.propertyType === 'Non-Rentable' || fs2.propertyType === 'Non-Rentable') {
        if (fs1.propertyType === 'Non-Rentable' && fs2.propertyType === 'Non-Rentable') {
          return 0;
        }
        if (fs1.propertyType === 'Non-Rentable') {
          return 1;
        }
        if (fs2.propertyType === 'Non-Rentable') {
          return -1;
        }
      }
      // sort by alphabetical order property type
      if (fs1.propertyType !== fs2.propertyType) {
        return fs1.propertyType.localeCompare(fs2.propertyType);
      }
    }
    // other than inactive sort or not a propertyType with asc sort
    else if (fs1[sort.active] !== fs2[sort.active]) {
      const sortMappingKeys = {
        sf: (s1: FloorFlatSpace, s2: FloorFlatSpace, multiplyBy: number) => (s1.sfRaw - s2.sfRaw) * multiplyBy,
      };

      const defaultFn = (s1: FloorFlatSpace, s2: FloorFlatSpace, multiplyBy: number, sortKey?: string) =>
        multiplyBy * `${s1[sortKey]}`.toLocaleLowerCase().localeCompare(`${s2[sortKey]}`.toLocaleLowerCase());

      const sortBy = sort.active === '' ? this.#sortDefaultOptions : sort;
      const multiplyBy = sortBy.direction === 'asc' ? 1 : -1;
      return (sortMappingKeys?.[sortBy.active] || defaultFn)(fs1, fs2, multiplyBy, sortBy.active);
    }

    // secondary sorting (SF, descending, then End Date, then Tenant)
    if (fs1.sfRaw === fs2.sfRaw) {
      if (this.#areDatesEqual(fs1, fs2)) {
        if ((fs1.tenantRaw || []).length == 0 && fs2.tenantRaw?.length > 0) {
          return 1;
        }
        if ((fs2.tenantRaw || []).length === 0) {
          return -1;
        }
        // Alphabethical order of the Tenant name, ascending
        const fs1Tenants = fs1.tenantRaw.sort((a, b) => a.localeCompare(b));
        const fs2Tenants = fs2.tenantRaw.sort((a, b) => a.localeCompare(b));

        return `${fs1Tenants[0] ?? ''}`.toLowerCase().localeCompare(`${fs2Tenants[0] ?? ''}`.toLowerCase());
      }
      // Sorted by the lowest End Date
      return this.#sortByDate(fs1, fs2);
    }
    // Sorted by SF highest to lowest
    return (fs1.sfRaw - fs2.sfRaw) * -1;
  };

  constructor(
    private dataHelper: PVFloorSpaceDataHelper,
    private spViewState: StackPlanViewStateService,
    @Inject(FLOOR_SPACE_DETAILS_STATE_HANDLER_TOKEN) private stateHandler: FloorSpaceDetailsHandlerSrv
  ) {}

  ngOnInit() {
    this.columns.set(floorSpaceDetailsTableColumns.map(c => ({ ...c })));
    this.dataHelper$ = this.dataHelper.getData().pipe(
      filter(Boolean),
      distinctUntilKeyChanged('floor_id'),
      tap(f => this.#title.next(this.#getFloorLabel(f) || DASH)),
      map(this.#getFlatSpaces)
    );

    this.spacesData$ = combineLatest([this.dataHelper$, this.sortSelection$]).pipe(
      untilDestroyed(this),
      map(this.sortFlatSpaces)
    );
  }

  onSortChange(sortEvt: Sort) {
    this.#sortSelection.next(sortEvt);
  }

  onClickTenant(row: FloorFlatSpace) {
    if (!row.disableShowFootPrintIcon) {
      this.stateHandler.nextState({ tenantIds: row.tenantIdList });
    }
  }
}
